import { useQuery } from "@apollo/client";
import { Button, CircularProgress, TextField } from "@material-ui/core";
import { AddCircleOutline } from "@material-ui/icons";
import { Autocomplete } from "@material-ui/lab";
import { pipe } from "fp-ts/lib/function";
import { head, prop } from "ramda";
import * as React from "react";
import { useState, useEffect } from "react";
import { PossibleEjiService, Query, Service } from "../../generated/nest-graphql";
import { GET_SERVICE_CATALOGUE } from "../../graphql/queries/getServiceCatalogue";
import { TECHNICIAN_GET_ME } from "../../graphql/queries/technicianGetMe";
import { possibleEJIServicesToFormValuesSpec } from "../../specs/ejiServicesSpec";
import { showErrorAlert } from "../../actions";
import { useDispatch } from "../../contexts/snackbar-context";

interface PossibleEJIServiceWithAddOnCapabilities extends PossibleEjiService {
  allowOnSideAddOn: boolean;
}

export const SelectServiceFormSection: React.FC<{
  setEditedServiceAndRecalculatePrice: any;
  servicesList: PossibleEjiService[];
  useMultiplePartOrders?: boolean;
}> = ({ setEditedServiceAndRecalculatePrice, servicesList, useMultiplePartOrders }) => {
  const dispatch = useDispatch();
  const [selectedService, setSelectedService] = useState<PossibleEJIServiceWithAddOnCapabilities>();

  const {
    data: serviceCatalogueData,
    loading: loadingServiceCatalogue,
    error: errorServiceCatalogue,
  } = useQuery<Query, {}>(GET_SERVICE_CATALOGUE);
  const {
    data: technicianGetMeData,
    loading: loadingTechnicianGetMe,
  } = useQuery<Query>(TECHNICIAN_GET_ME);

  useEffect(() => {
    if (errorServiceCatalogue) {
      showErrorAlert(dispatch, errorServiceCatalogue.message);
    }
  }, [errorServiceCatalogue, dispatch]);

  // technicianGetMe only supplies the home parts store, which handleClick already treats as optional.
  const loading = loadingServiceCatalogue || loadingTechnicianGetMe;

  if (loading) {
    return (
      <div className={"flex flex-row justify-center"}>
        <CircularProgress />
      </div>
    );
  }

  if (!serviceCatalogueData?.getServiceCatalogue) {
    return (
      <div className={"flex flex-col items-center text-center"}>
        <h2>Error loading services data</h2>
        <p>
          {errorServiceCatalogue?.message ? `${errorServiceCatalogue.message}. ` : ""}
          Close this dialog and try again, or contact support if it keeps happening.
        </p>
      </div>
    );
  }
  const services = prop("getServiceCatalogue")(serviceCatalogueData as any) as Service[];
  const servicesListWithAddonCapabilities = servicesList.map((possibleService) =>
    pipe(
      // this is a hack so that we dont have to include allowOnSiteAddOn for all possible services
      services.find((service) => service.id === possibleService.service),
      (matchingService): PossibleEJIServiceWithAddOnCapabilities => ({
        ...possibleService,
        allowOnSideAddOn: matchingService?.allowOnSiteAddOn ?? false,
      })
    )
  );
  const homePartsStore = technicianGetMeData?.technicianGetMe?.homePartsStore;
  return (
    <div>
      <Autocomplete
        value={selectedService?.name ?? ""}
        onChange={(_, selectedOption) => {
          setSelectedService(
            servicesListWithAddonCapabilities.find((service) => service.name === selectedOption) ?? null
          );
        }}
        options={servicesListWithAddonCapabilities
          .filter((service) => service.allowOnSideAddOn === true)
          .map((service) => service.name)}
        getOptionLabel={(option) => option}
        style={{ width: "100%" }}
        renderInput={(params) => <TextField {...params} label="Select Service" variant="outlined" />}
      />
      <div className={"pt-4"}>
        <Button
          variant="outlined"
          disabled={!selectedService}
          style={{ minWidth: "100%" }}
          startIcon={<AddCircleOutline />}
          onClick={() =>
            handleClick(selectedService, setEditedServiceAndRecalculatePrice, homePartsStore, useMultiplePartOrders)
          }
        >
          Confirm Selection
        </Button>
      </div>
    </div>
  );
};

const handleClick = (
  selectedService: PossibleEJIServiceWithAddOnCapabilities,
  setEditedServiceAndRecalculatePrice: any,
  homePartsStore: any,
  useMultiplePartOrders?: boolean
) => {
  const ejiService: PossibleEjiService = {
    ...selectedService,
    items: selectedService.items
      .filter(({ behavior }) => behavior === "Default")
      .map((item) => {
        const vendorUnitCost = item.expectedVendorUnitCost || item.vendorUnitCost;
        return {
          ...item,
          orderItem: {
            ...item.orderItem,
            partsStore: homePartsStore ?? null,
            vendorUnitCost: !useMultiplePartOrders ? vendorUnitCost : null,
            expectedVendorUnitCost: useMultiplePartOrders ? vendorUnitCost : null,
          },
        };
      }),
  };
  pipe([ejiService], possibleEJIServicesToFormValuesSpec, head, setEditedServiceAndRecalculatePrice);
};
